import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import { useSelector, useDispatch } from "react-redux";
import AlertDialog from "../components/AlertDialog";
import CircularLoading from "../components/CircularLoading";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import { api } from "common";
import { useTranslation } from "react-i18next";
import { colors } from "../components/Theme/WebTheme";
import Button from "components/CustomButtons/Button.js";
import { PanicSettings, BookingImageSettings } from "common/sharedFunctions";
import { DispatchSettings } from "common/sharedFunctions";
import { MenuItem, Select } from "@mui/material";

const useStyles = makeStyles((theme) => ({
    "@global": {
        body: {
            backgroundColor: theme.palette.common.white,
        },
    },
    container: {
        zIndex: "12",
        color: colors.WHITE,
        alignContent: "center",
    },
    container1: {
        backgroundColor: colors.WHITE,
        borderTopLeftRadius: "0px",
        borderTopRightRadius: "0px",
        borderBottomLeftRadius: "8px",
        borderBottomRightRadius: "8px",
        padding: "30px",
        width: "100%",
        top: "19px",
        boxShadow: "4px 4px 6px #9E9E9E",
    },
    gridcontainer: {
        alignContent: "center",
    },
    items: {
        margin: 0,
        width: "100%",
    },
    avatar: {
        margin: theme.spacing(1),
        backgroundColor: theme.palette.secondary.main,
        width: 192,
        height: 192,
    },
    form: {
        width: "100%",
        marginTop: theme.spacing(1),
        backgroundColor: colors.SECONDARY,
        alignContent: "center",
        borderRadius: "8px",
    },
    submit: {
        margin: theme.spacing(3, 0, 2),
    },
    rootRtl: {
        "& label": {
            right: 10,
            left: "auto",
            paddingRight: 20,
        },
        "& legend": {
            textAlign: "right",
            marginRight: 20,
        },
    },
    rootRtl_1: {
        "& label": {
            right: 15,
            left: "auto",
            paddingRight: 25,
        },
        "& legend": {
            textAlign: "right",
            marginRight: 25,
        },
    },
    rootRtl_2: {
        "& label": {
            right: 10,
            left: "auto",
            paddingRight: 12,
        },
        "& legend": {
            textAlign: "right",
            marginRight: 25,
        },
    },
    right: {
        "& legend": {
            marginRight: 30,
        },
    },
    rightStorelink: {
        "& legend": {
            marginRight: 25,
        },
    },
    title: {
        color: colors.WHITE,
        marginBottom: "15px",
        paddingTop: "15px",
        paddingLeft: "15px",
        fontSize: "20px",
    },
    buttonStyle: {
        margin: 0,
        width: "100%",
        height: 40,
        borderRadius: "30px",
        backgroundColor: colors.MAIN_COLOR,
        color: colors.WHITE,
    },
}));

const GeneralSettings = (props) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const { editSettings, clearSettingsViewError } = api;
    const settingsdata = useSelector((state) => state.settingsdata);
    const categorylistdata = useSelector((state) => state.categorylistdata);
    const featureFlags = useSelector((state) => state.featureflagsdata.data);
    const addonData = useSelector((state) => state.addondata.data);
    const dispatch = useDispatch();
    const classes = useStyles();
    const [data, setData] = useState();
    const [clicked, setClicked] = useState(false);
    const [carCategories, setCarCategories] = useState([]);

    useEffect(() => {
        if (settingsdata.settings) {
            setData(settingsdata.settings);
        }
    }, [settingsdata.settings]);

    useEffect(() => {
        if (categorylistdata.categories) {
            const categories = categorylistdata.categories
                .filter((item) => item.active)
                .map((category) => ({
                    label: t(category.category),
                    value: category.category,
                }));
            setCarCategories(categories);
        }
    }, [categorylistdata.categories, t]);

    const handleSwitchChange = (e) => {
        setData({ ...data, [e.target.name]: e.target.checked });
    };

    const handleTextChange = (e) => {
        setData({ ...data, [e.target.name]: e.target.value });
    };

    const handleMultiServiceChange = (e) => {
        setData({ ...data, configuration_multi_service: e.target.value });
    };

    const handleBonusChange = (e) => {
        setData({
            ...data,
            bonus:
                parseFloat(e.target.value) >= 0
                    ? parseFloat(e.target.value)
                    : "",
        });
    };

    const handleDriverRadius = (e) => {
        setData({
            ...data,
            driverRadius:
                parseFloat(e.target.value) >= 0
                    ? parseFloat(e.target.value)
                    : "",
        });
    };

    const handleNegativeBalanceLimit = (e) => {
        setData({
            ...data,
            negativeBalanceLimit:
                parseFloat(e.target.value) >= 0
                    ? parseFloat(e.target.value)
                    : "",
        });
    };
    // rebid code here
    const handleCounter = (e) => {
        setData({
            ...data,
            BidIncDecVal:
                parseFloat(e.target.value) >= 0
                    ? parseFloat(e.target.value)
                    : "10",
        });
    };

    const handleWalletMoney = (e) => {
        setData({ ...data, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (data.AllowCriticalEditsAdmin) {
            if (data.bonus === "") {
                alert(t("proper_bonus"));
            } else {
                setClicked(true);
                dispatch(editSettings(data));
            }
        } else {
            alert(t("demo_mode"));
        }
    };

    const handleClose = () => {
        setClicked(false);
        dispatch(clearSettingsViewError());
    };

    return data ? (
        <form className={classes.form}>
            <Grid item xs={12} sm={12}>
                <Grid item xs={12}>
                    <Typography
                        component="h1"
                        variant="h5"
                        className={classes.title}
                        style={{
                            textAlign: isRTL === "rtl" ? "right" : "left",
                        }}
                    >
                        {t("general_settings")}
                    </Typography>
                </Grid>
                <div className={classes.container1}>
                    <Grid
                        container
                        spacing={2}
                        style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
                    >
                        <Grid item xs={12} sm={12} md={6} lg={6}>
                            <Typography
                                component="h1"
                                variant="h5"
                                style={{
                                    textAlign:
                                        isRTL === "rtl" ? "right" : "left",
                                }}
                            >
                                {t("payment_settings")}
                            </Typography>
                            <FormControlLabel
                                style={{
                                    marginTop: "10px",
                                    flexDirection:
                                        isRTL === "rtl" ? "row-reverse" : "row",
                                }}
                                control={
                                    <Switch
                                        checked={data.disable_online}
                                        onChange={handleSwitchChange}
                                        name="disable_online"
                                        color="primary"
                                    />
                                }
                                label={t("disable_online")}
                            />
                            <FormControlLabel
                                style={{
                                    marginTop: "10px",
                                    flexDirection:
                                        isRTL === "rtl" ? "row-reverse" : "row",
                                }}
                                control={
                                    <Switch
                                        checked={data.disable_cash}
                                        onChange={handleSwitchChange}
                                        name="disable_cash"
                                        color="primary"
                                    />
                                }
                                label={t("disable_cash")}
                            />
                            <FormControlLabel
                                style={{
                                    marginTop: "10px",
                                    flexDirection:
                                        isRTL === "rtl" ? "row-reverse" : "row",
                                }}
                                control={
                                    <Switch
                                        checked={data.disable_yape}
                                        onChange={handleSwitchChange}
                                        name="disable_yape"
                                        color="primary"
                                    />
                                }
                                label={t("disable_yape")}
                            />
                            <Typography
                                component="h1"
                                variant="h5"
                                style={{
                                    marginTop: "15px",
                                    textAlign:
                                        isRTL === "rtl" ? "right" : "left",
                                }}
                            >
                                {t("security_title")}
                            </Typography>
                            <FormControlLabel
                                style={{
                                    flexDirection:
                                        isRTL === "rtl" ? "row-reverse" : "row",
                                }}
                                control={
                                    <Switch
                                        checked={data.otp_secure}
                                        onChange={handleSwitchChange}
                                        name="otp_secure"
                                        color="primary"
                                    />
                                }
                                label={t("settings_label3")}
                            />
                            <FormControlLabel
                                style={{
                                    flexDirection:
                                        isRTL === "rtl" ? "row-reverse" : "row",
                                }}
                                control={
                                    <Switch
                                        checked={data.driver_approval}
                                        onChange={handleSwitchChange}
                                        name="driver_approval"
                                        color="primary"
                                    />
                                }
                                label={t("settings_label4")}
                            />
                            <FormControlLabel
                                style={{
                                    flexDirection:
                                        isRTL === "rtl" ? "row-reverse" : "row",
                                }}
                                control={
                                    <Switch
                                        checked={data.carApproval}
                                        onChange={handleSwitchChange}
                                        name="carApproval"
                                        color="primary"
                                    />
                                }
                                label={t("settings_label5")}
                            />
                            <FormControlLabel
                                style={{
                                    flexDirection:
                                        isRTL === "rtl" ? "row-reverse" : "row",
                                }}
                                control={
                                    <Switch
                                        checked={data.imageIdApproval}
                                        onChange={handleSwitchChange}
                                        name="imageIdApproval"
                                        color="primary"
                                    />
                                }
                                label={t("settings_label6")}
                            />
                            {/* {featureFlags &&
                            featureFlags.add_on &&
                            addonData &&
                            addonData?.multi_service ? (
                                <FormControlLabel
                                    style={{
                                        marginTop: "10px",
                                        flexDirection:
                                            isRTL === "rtl"
                                                ? "row-reverse"
                                                : "row",
                                    }}
                                    control={
                                        <Switch
                                            checked={
                                                data.multi_service || false
                                            }
                                            onChange={handleSwitchChange}
                                            name="multi_service"
                                            color="primary"
                                        />
                                    }
                                    label={t("multi_services")}
                                />
                            ) : null} */}
                            {/* {featureFlags
                  && featureFlags.add_on
                  && addonData
                  && addonData?.multi_service
                  && data.multi_service
                  &&
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <Typography
                        component="h1"
                        variant="h5"
                        style={{ marginTop: '13px', marginBottom: '13px', textAlign: isRTL === 'rtl' ? 'right' : 'left', color: carCategories.length === 0 ? '#999' : 'inherit' }}
                      >
                        {t('configuration_multi_service')}
                      </Typography>
                      <Select
                        name="configuration_multi_service"
                        value={data.configuration_multi_service || ''}
                        fullWidth
                        onChange={(e) => {
                          handleMultiServiceChange(e);
                        }}
                        label={t("configuration_multi_service")}
                        disabled={carCategories.length === 0}
                      >
                        {carCategories.map((category) => (
                          <MenuItem key={category.value} value={category.value}>{t(category.label)}</MenuItem>
                        ))}
                      </Select>
                    </Grid>
                  </Grid>} */}
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={12} md={12} lg={12}>
                                    <Typography
                                        component="h1"
                                        variant="h5"
                                        style={{
                                            marginTop: "13px",
                                            textAlign:
                                                isRTL === "rtl"
                                                    ? "right"
                                                    : "left",
                                        }}
                                    >
                                        {t("referral_bonus")}
                                    </Typography>
                                    <TextField
                                        className={
                                            isRTL === "rtl"
                                                ? classes.rootRtl
                                                : null
                                        }
                                        variant="outlined"
                                        margin="normal"
                                        fullWidth
                                        id="bonus"
                                        label={t("referral_bonus")}
                                        name="bonus"
                                        autoComplete="bonus"
                                        onChange={handleBonusChange}
                                        value={data.bonus}
                                    />
                                </Grid>
                            </Grid>
                            <Typography
                                component="h1"
                                variant="h5"
                                style={{
                                    marginTop: "10px",
                                    textAlign:
                                        isRTL === "rtl" ? "right" : "left",
                                }}
                            >
                                {t("wallet_money_field")}
                            </Typography>
                            <TextField
                                className={
                                    isRTL === "rtl" ? classes.rootRtl : null
                                }
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                id="walletMoneyField"
                                label={t("wallet_money_field")}
                                name="walletMoneyField"
                                autoComplete="walletMoneyField"
                                onChange={handleWalletMoney}
                                value={data.walletMoneyField}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} lg={6}>
                            <Typography
                                component="h1"
                                variant="h5"
                                style={{
                                    textAlign:
                                        isRTL === "rtl" ? "right" : "left",
                                }}
                            >
                                {t("advance_settings")}
                            </Typography>
                            <FormControlLabel
                                style={{
                                    marginTop: "10px",
                                    flexDirection:
                                        isRTL === "rtl" ? "row-reverse" : "row",
                                }}
                                control={
                                    <Switch
                                        checked={data.convert_to_mile}
                                        onChange={handleSwitchChange}
                                        name="convert_to_mile"
                                        color="primary"
                                    />
                                }
                                label={t("convert_to_mile")}
                            />
                            <FormControlLabel
                                style={{
                                    marginTop: "10px",
                                    flexDirection:
                                        isRTL === "rtl" ? "row-reverse" : "row",
                                }}
                                control={
                                    <Switch
                                        checked={data.CarHornRepeat}
                                        onChange={handleSwitchChange}
                                        name="CarHornRepeat"
                                        color="primary"
                                    />
                                }
                                label={t("car_horn_repeat")}
                            />
                            <BookingImageSettings
                                data={data}
                                handleSwitchChange={handleSwitchChange}
                            />
                            <FormControlLabel
                                style={{
                                    marginTop: "10px",
                                    flexDirection:
                                        isRTL === "rtl" ? "row-reverse" : "row",
                                }}
                                control={
                                    <Switch
                                        checked={data.UseRebidMode}
                                        onChange={handleSwitchChange}
                                        name="UseRebidMode"
                                        color="primary"
                                    />
                                }
                                label={t("use_rebid_mode")}
                            />
                            <FormControlLabel
                                style={{
                                    marginTop: "10px",
                                    flexDirection:
                                        isRTL === "rtl" ? "row-reverse" : "row",
                                }}
                                control={
                                    <Switch
                                        checked={data.RiderWithDraw}
                                        onChange={handleSwitchChange}
                                        name="RiderWithDraw"
                                        color="primary"
                                    />
                                }
                                label={t("rider_withdraw")}
                            />
                            <FormControlLabel
                                style={{
                                    marginTop: "10px",
                                    flexDirection:
                                        isRTL === "rtl" ? "row-reverse" : "row",
                                }}
                                control={
                                    <Switch
                                        checked={data.useDistanceMatrix}
                                        onChange={handleSwitchChange}
                                        name="useDistanceMatrix"
                                        color="primary"
                                    />
                                }
                                label={t("use_distance_matrix")}
                            />
                            <FormControlLabel
                                style={{
                                    marginTop: "10px",
                                    flexDirection:
                                        isRTL === "rtl" ? "row-reverse" : "row",
                                }}
                                control={
                                    <Switch
                                        checked={data.FacebookLoginEnabled}
                                        onChange={handleSwitchChange}
                                        name="FacebookLoginEnabled"
                                        color="primary"
                                    />
                                }
                                label={t("facebook_login")}
                            />
                            <FormControlLabel
                                style={{
                                    marginTop: "10px",
                                    flexDirection:
                                        isRTL === "rtl" ? "row-reverse" : "row",
                                }}
                                control={
                                    <Switch
                                        checked={data.AppleLoginEnabled}
                                        onChange={handleSwitchChange}
                                        name="AppleLoginEnabled"
                                        color="primary"
                                    />
                                }
                                label={t("apple_login")}
                            />
                            <Typography
                                component="h1"
                                variant="h5"
                                style={{
                                    marginTop: "15px",
                                    textAlign:
                                        isRTL === "rtl" ? "right" : "left",
                                }}
                            >
                                {t("rebid_counter_val")}
                            </Typography>
                            <TextField
                                className={
                                    isRTL === "rtl" ? classes.rootRtl : null
                                }
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                id="BidIncDecVal"
                                label={t("cntr_value")}
                                name="BidIncDecVal"
                                autoComplete="BidIncDecVal"
                                onChange={handleCounter}
                                value={data.BidIncDecVal}
                            />
                            <Typography
                                component="h1"
                                variant="h5"
                                style={{
                                    marginTop: "15px",
                                    textAlign:
                                        isRTL === "rtl" ? "right" : "left",
                                }}
                            >
                                {t("driver_setting")}
                            </Typography>
                            <TextField
                                className={
                                    isRTL === "rtl" ? classes.rootRtl : null
                                }
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                id="driverRadius"
                                label={t("driverRadius")}
                                name="driverRadius"
                                autoComplete="driverRadius"
                                onChange={handleDriverRadius}
                                value={data.driverRadius}
                            />
                            <DispatchSettings
                                autoDispatch={data.autoDispatch}
                                onChange={handleSwitchChange}
                            />
                            <FormControlLabel
                                style={{
                                    flexDirection:
                                        isRTL === "rtl" ? "row-reverse" : "row",
                                }}
                                control={
                                    <Switch
                                        checked={data.negativeBalance}
                                        onChange={handleSwitchChange}
                                        name="negativeBalance"
                                        color="primary"
                                    />
                                }
                                label={t("negative_balance")}
                            />
                            {data.negativeBalance && (
                                <TextField
                                    className={
                                        isRTL === "rtl" ? classes.rootRtl : null
                                    }
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                    id="negativeBalanceLimit"
                                    label={t("negative_balance_limit") || "Límite de saldo negativo"}
                                    name="negativeBalanceLimit"
                                    autoComplete="negativeBalanceLimit"
                                    type="number"
                                    onChange={handleNegativeBalanceLimit}
                                    value={data.negativeBalanceLimit || ""}
                                    helperText={
                                        t("negative_balance_limit_helper") ||
                                        "Ingrese el valor máximo permitido para saldo negativo del conductor"
                                    }
                                />
                            )}
                            <FormControlLabel
                                style={{
                                    flexDirection:
                                        isRTL === "rtl" ? "row-reverse" : "row",
                                }}
                                control={
                                    <Switch
                                        checked={data.showLiveRoute}
                                        onChange={handleSwitchChange}
                                        name="showLiveRoute"
                                        color="primary"
                                    />
                                }
                                label={t("show_live_route")}
                            />

                            {/* <FormControlLabel
                  style={{flexDirection: isRTL === 'rtl' ? 'row-reverse' : 'row' }}
                  control={
                    <Switch
                      checked={data.carType_required}
                      onChange={handleSwitchChange}
                      name="carType_required"
                      color="primary"
                    />
                  }
                  label={t('carType_required')}
                /> */}

                            <FormControlLabel
                                style={{
                                    flexDirection:
                                        isRTL === "rtl" ? "row-reverse" : "row",
                                }}
                                control={
                                    <Switch
                                        checked={data.term_required}
                                        onChange={handleSwitchChange}
                                        name="term_required"
                                        color="primary"
                                    />
                                }
                                label={t("term_required")}
                            />

                            <FormControlLabel
                                style={{
                                    flexDirection:
                                        isRTL === "rtl" ? "row-reverse" : "row",
                                }}
                                control={
                                    <Switch
                                        checked={data.license_image_required}
                                        onChange={handleSwitchChange}
                                        name="license_image_required"
                                        color="primary"
                                    />
                                }
                                label={t("license_image_required")}
                            />

                            <PanicSettings
                                classes={classes}
                                data={data}
                                handleTextChange={handleTextChange}
                            />
                        </Grid>
                    </Grid>
                    <Grid container>
                        <Grid item xs={12} sm={12} md={6} lg={6}>
                            <Button
                                size="lg"
                                onClick={handleSubmit}
                                variant="contained"
                                color="success"
                                className={classes.buttonStyle}
                            >
                                {t("submit")}
                            </Button>
                        </Grid>
                    </Grid>
                </div>
                <AlertDialog
                    open={settingsdata.error.flag && clicked}
                    onClose={handleClose}
                >
                    {t("update_failed")}
                </AlertDialog>
            </Grid>
        </form>
    ) : (
        <CircularLoading />
    );
};

export default GeneralSettings;
